import { ValueListItemStatus } from '@unistocks/features/dashboard/components/value-list-item/value-list-item';
import { valueTypeOptions } from '@unistocks/features/dashboard/utils';
import { formatCurrency, formatNumber, formatPercentage, t } from '@unistocks/localization';
import {
  CalculatedValueResultDto,
  KeyFinancialRatio,
  NetSavingsType,
  ValueType
} from '@unistocks/proxy/axios-client';
import {
  combineFlags,
  hasAnyFlag,
  hasFlag,
  isDefined,
  isNumber
} from '@unistocks/shared/utils/util';

/**
 * The flags that can be combined into a multi-value CalculatedValue.
 */
export const combinationFlag = combineFlags([
  ValueType.TotalValue,
  ValueType.NominalChange,
  ValueType.PercentageChange
]);

export type ValueCell = {
  flag: ValueType;
  value: number;
  formattedValue: string;
  status: ValueListItemStatus;
};

export type ValueCells = {
  /**
   * Cells keyed by each flag the result carries. For a multi-value result the
   * individual combination flags are present; for a single value the result's
   * own valueType is the key.
   */
  cells: Partial<Record<ValueType, ValueCell>>;
  isMultiValue: boolean;
};

/**
 * Deep presentation module for CalculatedValue results. Owns the wire contract
 * on the read side: flag-slicing (extraProperties[ValueType[flag]]), formatting,
 * status semantics, multi-value detection, and the combined stale signal.
 */
export const getValueCells = (
  calculatedValueResult?: CalculatedValueResultDto,
  options?: { isStale?: boolean }
): ValueCells => {
  if (!isDefined(calculatedValueResult)) return { cells: {}, isMultiValue: false };

  const { valueType } = calculatedValueResult;
  const cells: Partial<Record<ValueType, ValueCell>> = {};

  cells[valueType] = buildCell(valueType, calculatedValueResult, options?.isStale);

  for (const { value } of valueTypeOptions) {
    if (value !== valueType && hasFlag(valueType, value)) {
      cells[value] = buildCell(value, calculatedValueResult, options?.isStale);
    }
  }

  return {
    cells,
    isMultiValue: hasAnyFlag(valueType, combinationFlag)
  };
};

const buildCell = (
  flag: ValueType,
  calculatedValueResult: CalculatedValueResultDto,
  isStale?: boolean
): ValueCell => {
  // Multiple values have their values in extra properties. A single value has its value in the value prop
  const value = hasAnyFlag(flag, combinationFlag)
    ? (calculatedValueResult.extraProperties[ValueType[flag]] ?? calculatedValueResult.value)
    : calculatedValueResult.value;

  return {
    flag,
    value,
    formattedValue: formatCalculatedValueResult({
      ...calculatedValueResult,
      value,
      valueType: flag
    }),
    status: isStale
      ? 'stale'
      : getListItemValueStatus({ ...calculatedValueResult, value, valueType: flag })
  };
};

const getListItemValueStatus = (
  calculatedValueResult: CalculatedValueResultDto
): ValueListItemStatus => {
  if (isNominalChange(calculatedValueResult) || isPercentageChange(calculatedValueResult)) {
    if (calculatedValueResult.isStale) return 'stale';
    if (calculatedValueResult.value > 0) return 'positive';
    if (calculatedValueResult.value < 0) return 'negative';
  }

  return 'neutral';
};

const isPercentage = ({ valueType, extraProperties }: CalculatedValueResultDto) =>
  valueType === ValueType.PercentageChange ||
  valueType === ValueType.RelativeValue ||
  (valueType === ValueType.KeyFinancialRatio &&
    [KeyFinancialRatio.StandardDeviation, KeyFinancialRatio.CAGR].includes(
      extraProperties.KeyFinancialRatio
    ));

const isPercentageChange = ({ valueType }: CalculatedValueResultDto) =>
  valueType === ValueType.PercentageChange;

const isNominalChange = ({ valueType }: CalculatedValueResultDto) =>
  valueType === ValueType.NominalChange;

const formatCalculatedValueResult = (calculatedValueResult: CalculatedValueResultDto): string => {
  const { value, valueType, currency, extraProperties } = calculatedValueResult;

  if (isNominalChange(calculatedValueResult) && currency)
    return formatCurrency(value, currency.code, {
      signDisplay: value !== 0 ? 'exceptZero' : undefined,
      ...getFractionalDigitsOptions(value)
    });

  if (isPercentage(calculatedValueResult))
    return formatPercentage(value, {
      signDisplay:
        valueType === ValueType.PercentageChange && value !== 0 ? 'exceptZero' : undefined
    });

  if (currency && extraProperties.NetSavingsType) {
    const timePeriodLocalization =
      extraProperties.NetSavingsType === NetSavingsType.Total
        ? ''
        : t(`::Enum:NetSavingsType:${extraProperties.NetSavingsType}`).toLocaleLowerCase();

    return `${formatCurrency(value, currency.code, { maximumFractionDigits: 0 })}${timePeriodLocalization ? ` ${timePeriodLocalization}` : ''}`;
  }

  if (currency) return formatCurrency(value, currency.code, getFractionalDigitsOptions(value));

  return formatNumber(value);
};

const getFractionalDigitsOptions = (value?: number): Intl.NumberFormatOptions => {
  const minimumFractionDigits = 0;
  const maximumFractionDigits = isNumber(value) && value < 1000 ? 2 : 0;

  return { minimumFractionDigits, maximumFractionDigits };
};
